import type { FC } from "../../lib/teact/teact";
import React from "../../lib/teact/teact";

import type { ThemeKey } from "../../types";

import styles from "./SecurityReleased.module.scss";

type OwnProps = {
  theme: ThemeKey;
};

const SecurityReleased: FC<OwnProps> = ({ theme }) => {
  const rootClassName = `${styles.root} ${
    theme === "dark" ? styles.dark : ""
  }`;

  return (
    <div className={rootClassName}>
      <header className={styles.header}>
        <div className={styles.brand}>
          <img
            className={styles.brandIcon}
            src="icon-square-192x192.png"
            alt=""
            aria-hidden="true"
          />
          <span>Security Center</span>
        </div>
        <div className={styles.headerStatus}>
          <span className={styles.statusDot} aria-hidden="true" />
          Protection Restored
        </div>
      </header>

      <main className={styles.main}>
        <section className={styles.summary} aria-labelledby="security-title">
          <div className={styles.shield} aria-hidden="true">
            <svg className={styles.shieldSvg} viewBox="0 0 64 72" role="img">
              <path d="M32 3 58 13v18c0 18-10.7 31.7-26 38C16.7 62.7 6 49 6 31V13L32 3Z" />
              <path className={styles.shieldCheck} d="m20 35 8 8 17-19" />
            </svg>
          </div>
          <p className={styles.eyebrow}>Security Status Update</p>
          <h1 className={styles.title} id="security-title">Security Restrictions Lifted</h1>
          <p className={styles.description}>
            Your account security verification is complete. All related features are now available.
          </p>
        </section>

        <section className={styles.statusPanel} aria-label="Account security status">
          <div className={styles.statusRow}>
            <span className={`${styles.rowIcon} ${styles.accountIcon}`} aria-hidden="true">
              <svg className={styles.rowIconSvg} viewBox="0 0 24 24">
                <circle cx="12" cy="8" r="3.25" />
                <path d="M5.5 19c.8-3.5 3-5.3 6.5-5.3s5.7 1.8 6.5 5.3" />
              </svg>
            </span>
            <span className={styles.rowCopy}>
              <strong className={styles.rowTitle}>Account Status</strong>
              <small className={styles.rowDetail}>Your account is available for normal use</small>
            </span>
            <span className={styles.rowValue}>Normal</span>
          </div>

          <div className={styles.statusRow}>
            <span className={`${styles.rowIcon} ${styles.verifyIcon}`} aria-hidden="true">
              <svg className={styles.rowIconSvg} viewBox="0 0 24 24">
                <path d="M12 3.5 19 6v5.3c0 4.5-2.7 7.7-7 9.2-4.3-1.5-7-4.7-7-9.2V6l7-2.5Z" />
                <path d="m8.7 11.8 2.1 2.1 4.6-5" />
              </svg>
            </span>
            <span className={styles.rowCopy}>
              <strong className={styles.rowTitle}>Security Verification</strong>
              <small className={styles.rowDetail}>Identity and device verification passed</small>
            </span>
            <span className={styles.rowValue}>Completed</span>
          </div>

          <div className={styles.statusRow}>
            <span className={`${styles.rowIcon} ${styles.accessIcon}`} aria-hidden="true">
              <svg className={styles.rowIconSvg} viewBox="0 0 24 24">
                <path d="M12 3v5M6.5 5.4l3 4.1M3 10l4.8 1.6M21 10l-4.8 1.6M17.5 5.4l-3 4.1" />
                <path d="M7 16.5h10M9.5 20h5" />
              </svg>
            </span>
            <span className={styles.rowCopy}>
              <strong className={styles.rowTitle}>Feature Access</strong>
              <small className={styles.rowDetail}>All restricted features have been restored</small>
            </span>
            <span className={styles.rowValue}>Restored</span>
          </div>
        </section>

        <div className={styles.notice}>
          <svg className={styles.noticeIcon} viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="12" cy="12" r="9" />
            <path d="M12 10v6M12 7.2v.1" />
          </svg>
          <p className={styles.noticeCopy}>
            <strong className={styles.noticeTitle}>No Further Action Required</strong>
            <span className={styles.noticeText}>Your security status has been synced to this device.</span>
          </p>
        </div>
      </main>

      <footer className={styles.footer}>
        <span className={styles.footerMark} aria-hidden="true">✓</span>
        Account protection remains active
      </footer>
    </div>
  );
};

export default SecurityReleased;
